import { useCallback, useRef } from 'react';
import GenreSelector from './GenreSelector';
import '../styles/player.css';

function PreviewPlayer({
  currentTrack,
  isPlaying,
  isLoading,
  progress,
  onToggle,
  onSeek,
  onScrubbing,
  onNext,
  onPrev,
  mode,
  onModeToggle,
  currentIndex,
  totalCount,
  availableGenres,
  selectedGenres,
  onGenreSelectionChange,
  showGenreFilter,
  extraControls,
  onArtistClick,
}) {
  // Use a callback ref so the ResizeObserver is set up/torn down exactly when
  // the player div appears/disappears in the DOM. The previous useEffect+useRef
  // approach failed because the effect ran once on mount when the component
  // returned null (no track), so the observer was never attached.
  const cleanupRef = useRef(null);

  const playerRef = useCallback((el) => {
    // Tear down previous observer if any
    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = null;
    }

    if (!el) return;

    const update = () => {
      requestAnimationFrame(() => {
        const parent = el.parentElement;
        if (!parent) return;
        // Use offsetHeight instead of getBoundingClientRect — it returns
        // the layout height unaffected by CSS transforms (like the
        // slide-up entry animation), so the value is correct even when
        // the observer fires mid-animation.
        const height = el.offsetHeight;
        if (height > 0) {
          parent.style.setProperty('--preview-player-top', `${height}px`);
        }
      });
    };

    const observer = new ResizeObserver(update);
    observer.observe(el);
    update();

    window.addEventListener('resize', update);

    cleanupRef.current = () => {
      observer.disconnect();
      window.removeEventListener('resize', update);
      el.parentElement?.style.removeProperty('--preview-player-top');
    };
  }, []);

  const progressBarRef = useRef(null);
  const progressFillRef = useRef(null);
  const scrubbing = useRef(false);
  const rafId = useRef(null);
  const pendingClientX = useRef(null);

  const commitSeek = useCallback(
    (clientX) => {
      const bar = progressBarRef.current;
      if (!bar) return;
      const rect = bar.getBoundingClientRect();
      const fraction = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      // Update fill bar directly via DOM — avoids React re-render per pixel
      if (progressFillRef.current) {
        progressFillRef.current.style.width = `${fraction * 100}%`;
      }
      onSeek(fraction);
    },
    [onSeek]
  );

  const handlePointerDown = useCallback(
    (e) => {
      scrubbing.current = true;
      onScrubbing?.(true);
      e.currentTarget.setPointerCapture(e.pointerId);
      commitSeek(e.clientX);
    },
    [commitSeek, onScrubbing]
  );

  const handlePointerMove = useCallback(
    (e) => {
      if (!scrubbing.current) return;
      // Stash the latest position; only commit via RAF to avoid oversaturation
      pendingClientX.current = e.clientX;
      if (rafId.current) return;
      rafId.current = requestAnimationFrame(() => {
        rafId.current = null;
        if (pendingClientX.current !== null && scrubbing.current) {
          commitSeek(pendingClientX.current);
        }
      });
    },
    [commitSeek]
  );

  const handlePointerUp = useCallback(() => {
    scrubbing.current = false;
    onScrubbing?.(false);
    pendingClientX.current = null;
    if (rafId.current) {
      cancelAnimationFrame(rafId.current);
      rafId.current = null;
    }
  }, [onScrubbing]);

  if (!currentTrack && !isLoading) return null;

  const totalSeconds = 30;
  const elapsed = Math.floor((progress || 0) * totalSeconds);
  const remaining = totalSeconds - elapsed;
  const fmtElapsed = `${Math.floor(elapsed / 60)}:${(elapsed % 60).toString().padStart(2, '0')}`;
  const fmtRemaining = `-${Math.floor(remaining / 60)}:${(remaining % 60).toString().padStart(2, '0')}`;

  return (
    <div ref={playerRef} className="preview-player">
      {/* Full-width progress bar across the top */}
      <div
        className="player-progress-bar"
        ref={progressBarRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        <div
          ref={progressFillRef}
          className="player-progress-fill"
          style={{ width: `${(progress || 0) * 100}%` }}
        />
      </div>

      <div className="player-main">
        <div className="player-left">
          <button
            className={`player-mode-btn ${mode === 'shuffle' ? 'active' : ''}`}
            onClick={onModeToggle}
            title={mode === 'shuffle' ? 'Shuffle on — click for sequential' : 'Sequential — click to shuffle'}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
              <polyline points="16 3 21 3 21 8" />
              <line x1="4" y1="20" x2="21" y2="3" />
              <polyline points="21 16 21 21 16 21" />
              <line x1="15" y1="15" x2="21" y2="21" />
              <line x1="4" y1="4" x2="9" y2="9" />
            </svg>
            Shuffle
          </button>

          {showGenreFilter && availableGenres.length > 0 && (
            <GenreSelector
              genres={availableGenres}
              selectedGenres={selectedGenres}
              onSelectionChange={onGenreSelectionChange}
            />
          )}

          {extraControls}

          <span className="player-position">
            {currentIndex + 1} / {totalCount}
          </span>
        </div>

        <div className="player-center">
          <span className="player-time player-time-left">{fmtElapsed}</span>

          <button className="player-nav-btn" onClick={onPrev} title="Previous">
            <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
              <polygon points="19,20 9,12 19,4" />
              <rect x="5" y="4" width="2" height="16" />
            </svg>
          </button>

          <button className="player-toggle" onClick={onToggle}>
            {isPlaying ? (
              <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                <rect x="6" y="4" width="4" height="16" rx="1" />
                <rect x="14" y="4" width="4" height="16" rx="1" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                <polygon points="5,3 19,12 5,21" />
              </svg>
            )}
          </button>

          <button className="player-nav-btn" onClick={onNext} title="Next">
            <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
              <polygon points="5,4 15,12 5,20" />
              <rect x="17" y="4" width="2" height="16" />
            </svg>
          </button>

          <span className="player-time player-time-right">{fmtRemaining}</span>
        </div>

        <div className={`player-right${onArtistClick ? ' player-right-clickable' : ''}`} onClick={onArtistClick} role={onArtistClick ? 'button' : undefined} tabIndex={onArtistClick ? 0 : undefined} onKeyDown={onArtistClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onArtistClick(); } } : undefined}>
          {currentTrack?.albumImage ? (
            <img
              className="player-album-art"
              src={currentTrack.albumImage}
              alt={currentTrack.albumName || 'Album'}
            />
          ) : (
            <div className="player-album-art player-album-placeholder">
              <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z" />
              </svg>
            </div>
          )}
          <div className="player-info">
            <span className="player-track-name">
              {isLoading ? 'Loading...' : currentTrack?.name || ''}
            </span>
            <span className="player-artist-name">
              {currentTrack?.artistName || ''}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PreviewPlayer;
